/**
 * Règlement des sanctions — barème officiel d'OpSecours (règlement « RG »).
 *
 * SOURCE DE VÉRITÉ UNIQUE : cette liste alimente
 *  - la page publique du Règlement des sanctions (ReglementSanctions.tsx) ;
 *  - (à venir) le sélecteur de motifs dans la modale de sanction du panel ;
 *  - (à venir) le texte des annonces de sanction diffusées en jeu.
 *
 * Chaque infraction porte un code hiérarchique « section.article » (ex. « 4.1 »)
 * rattaché au règlement « RG » (Règlement Général). Le staff sanctionne avec un
 * ou plusieurs codes ; l'annonce n'affiche que les codes (ex. « 4.1, 2.1 RG »).
 *
 * Module volontairement sans dépendance (TS pur) afin d'être réutilisable côté
 * client comme côté serveur.
 */

/** Identifiant du règlement dont dépendent les codes (suffixe affiché : « RG »). */
export const REGLEMENT_ID = 'RG';

export type SanctionKind = 'mute' | 'ban_temp' | 'ban_perm';

export interface SanctionLevel {
  kind: SanctionKind;
  /** Durée en heures pour « mute » et « ban_temp ». Ignoré pour « ban_perm ». */
  durationHours?: number;
}

export interface RecommendedSanction {
  /** Sanction de base (1re infraction). */
  base: SanctionLevel;
  /** Sanction en cas de récidive (optionnelle). */
  repeat?: SanctionLevel;
}

export interface ReglementArticle {
  /** Code hiérarchique, ex. « 4.1 ». */
  code: string;
  labelFr: string;
  labelEn: string;
  sanction: RecommendedSanction;
}

export interface ReglementSection {
  /** Numéro de section = premier segment du code (ex. section 4 → codes « 4.x »). */
  number: number;
  titleFr: string;
  titleEn: string;
  articles: ReglementArticle[];
}

const DAY = 24; // heures

export const REGLEMENT_SECTIONS: ReglementSection[] = [
  {
    number: 1,
    titleFr: 'Compte & accès',
    titleEn: 'Account & access',
    articles: [
      { code: '1.1', labelFr: 'Multi-comptes', labelEn: 'Multi-accounting',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY }, repeat: { kind: 'ban_perm' } } },
      { code: '1.2', labelFr: 'Partage / prêt de compte', labelEn: 'Account sharing / lending',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '1.3', labelFr: 'Vente / échange de compte', labelEn: 'Account selling / trading',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '1.4', labelFr: 'Contournement de sanction (ban evasion)', labelEn: 'Sanction evasion (ban evasion)',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '1.5', labelFr: "Usurpation d'identité (joueur)", labelEn: 'Identity theft (player)',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
      { code: '1.6', labelFr: "Usurpation d'identité staff", labelEn: 'Staff impersonation',
        sanction: { base: { kind: 'ban_perm' } } },
    ],
  },
  {
    number: 2,
    titleFr: 'Communication & respect',
    titleEn: 'Communication & respect',
    articles: [
      { code: '2.1', labelFr: 'Insultes / manque de respect', labelEn: 'Insults / disrespect',
        sanction: { base: { kind: 'mute', durationHours: 24 }, repeat: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '2.2', labelFr: 'Comportement toxique / provocation', labelEn: 'Toxic behavior / provocation',
        sanction: { base: { kind: 'mute', durationHours: 12 } } },
      { code: '2.3', labelFr: 'Harcèlement / acharnement', labelEn: 'Harassment',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
      { code: '2.4', labelFr: 'Propos haineux, racistes, discriminatoires', labelEn: 'Hateful, racist or discriminatory remarks',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '2.5', labelFr: 'Contenu choquant / NSFW', labelEn: 'Shocking / NSFW content',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
      { code: '2.6', labelFr: 'Spam / flood', labelEn: 'Spam / flood',
        sanction: { base: { kind: 'mute', durationHours: 12 } } },
      { code: '2.7', labelFr: 'Publicité / promotion externe', labelEn: 'Advertising / external promotion',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '2.8', labelFr: 'Divulgation de données personnelles (doxxing)', labelEn: 'Disclosure of personal data (doxxing)',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '2.9', labelFr: 'Menaces', labelEn: 'Threats',
        sanction: { base: { kind: 'ban_perm' } } },
    ],
  },
  {
    number: 3,
    titleFr: 'Jeu & économie',
    titleEn: 'Gameplay & economy',
    articles: [
      { code: '3.1', labelFr: 'Abus de bug / exploit', labelEn: 'Bug / exploit abuse',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
      { code: '3.2', labelFr: "Non-signalement d'un bug exploité", labelEn: 'Failure to report an exploited bug',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '3.3', labelFr: 'Manipulation / triche économique', labelEn: 'Economic manipulation / cheating',
        sanction: { base: { kind: 'ban_temp', durationHours: 14 * DAY }, repeat: { kind: 'ban_perm' } } },
      { code: '3.4', labelFr: 'Comportement anti-jeu / sabotage', labelEn: 'Anti-game behavior / sabotage',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
      { code: '3.5', labelFr: "Abus du système d'interventions / CTA", labelEn: 'Abuse of the intervention / CTA system',
        sanction: { base: { kind: 'mute', durationHours: 24 }, repeat: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '3.6', labelFr: 'Abus alliance / cagnotte', labelEn: 'Alliance / pool abuse',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
    ],
  },
  {
    number: 4,
    titleFr: 'Triche & sécurité',
    titleEn: 'Cheating & security',
    articles: [
      { code: '4.1', labelFr: 'Logiciels malveillants / triche', labelEn: 'Malicious software / cheating',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '4.2', labelFr: 'Automatisation / bot / script / macro', labelEn: 'Automation / bot / script / macro',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '4.3', labelFr: "Tentative d'attaque / faille de sécurité", labelEn: 'Attack attempt / security breach',
        sanction: { base: { kind: 'ban_perm' } } },
      { code: '4.4', labelFr: "Exploitation / partage d'une faille", labelEn: 'Exploiting / sharing a vulnerability',
        sanction: { base: { kind: 'ban_perm' } } },
    ],
  },
  {
    number: 5,
    titleFr: 'Staff & support',
    titleEn: 'Staff & support',
    articles: [
      { code: '5.1', labelFr: 'Faux signalement / abus du support', labelEn: 'False report / support abuse',
        sanction: { base: { kind: 'mute', durationHours: 24 } } },
      { code: '5.2', labelFr: 'Manque de respect envers le staff', labelEn: 'Disrespect toward staff',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '5.3', labelFr: 'Refus de coopérer avec le staff', labelEn: 'Refusal to cooperate with staff',
        sanction: { base: { kind: 'ban_temp', durationHours: 3 * DAY } } },
      { code: '5.4', labelFr: 'Tentative de corruption du staff', labelEn: 'Attempt to corrupt staff',
        sanction: { base: { kind: 'ban_temp', durationHours: 7 * DAY } } },
    ],
  },
];

/** Accès direct à un article par son code (ex. getArticleByCode('4.1')). */
export const REGLEMENT_ARTICLES_BY_CODE: Record<string, ReglementArticle> =
  REGLEMENT_SECTIONS.reduce<Record<string, ReglementArticle>>((acc, section) => {
    for (const article of section.articles) acc[article.code] = article;
    return acc;
  }, {});

export const getArticleByCode = (code: string): ReglementArticle | undefined =>
  REGLEMENT_ARTICLES_BY_CODE[code];

/** Sévérité d'un type de sanction : sert à choisir la plus forte parmi plusieurs codes. */
export const sanctionSeverity = (kind: SanctionKind): number =>
  kind === 'ban_perm' ? 3 : kind === 'ban_temp' ? 2 : 1;

/** Formate une durée en heures (« 12 h », « 24 h », « 3 j », « 14 j »…). */
export const formatDuration = (hours: number | undefined, lang: 'fr' | 'en' = 'fr'): string => {
  if (!hours || hours <= 0) return '';
  // On n'exprime en jours qu'à partir de 48 h (un mute de 24 h reste « 24 h »).
  if (hours >= 48 && hours % 24 === 0) {
    const days = hours / 24;
    return lang === 'fr' ? `${days} j` : `${days} d`;
  }
  return `${hours} h`;
};

/** Formate un niveau de sanction (« Mute 24 h », « Ban 7 j », « Ban définitif »). */
export const formatSanctionLevel = (level: SanctionLevel, lang: 'fr' | 'en' = 'fr'): string => {
  const fr = lang === 'fr';
  switch (level.kind) {
    case 'ban_perm':
      return fr ? 'Ban définitif' : 'Permanent ban';
    case 'ban_temp':
      return `${fr ? 'Ban' : 'Ban'} ${formatDuration(level.durationHours, lang)}`.trim();
    case 'mute':
      return `${fr ? 'Mute' : 'Mute'} ${formatDuration(level.durationHours, lang)}`.trim();
  }
};

/** Formate la sanction recommandée avec sa récidive éventuelle (« Mute 24 h → Ban 3 j »). */
export const formatRecommendedSanction = (s: RecommendedSanction, lang: 'fr' | 'en' = 'fr'): string => {
  const base = formatSanctionLevel(s.base, lang);
  return s.repeat ? `${base} → ${formatSanctionLevel(s.repeat, lang)}` : base;
};

/** Construit la référence courte d'un ou plusieurs codes (ex. « 4.1, 2.1 RG »). */
export const formatCodesRef = (codes: string[]): string =>
  codes.length ? `${codes.join(', ')} ${REGLEMENT_ID}` : '';
